/**
 * Espejo del catálogo de permisos del backend (services/permission_catalog.py).
 *
 * Uso recomendado: importa `PERMISSIONS.MANAGE_USERS` en lugar de la cadena
 * cruda para que un cambio en el backend rompa el build en vez de
 * silenciosamente.
 *
 * NO capar UI por nombre de rol — usar siempre `can(permission)` o
 * `canAny([permissions])` desde `useAuth()`.
 */

export const PERMISSIONS = {
  MANAGE_USERS: "manage_users",
  MANAGE_ROLES: "manage_roles",
  MANAGE_ORGANIZATION_CONFIG: "manage_organization_config",
  MANAGE_MEMBERS: "manage_members",
  VIEW_MEMBERS: "view_members",
  MANAGE_REMESAS: "manage_remesas",
  VIEW_REMESAS: "view_remesas",
  MANAGE_RECIBOS: "manage_recibos",
  VIEW_RECIBOS: "view_recibos",
  MANAGE_EVENTOS: "manage_eventos",
  MANAGE_DOCUMENTS: "manage_documents",
  VIEW_PRIVATE_DOCUMENTS: "view_private_documents",
  MANAGE_PERSONAL_DOCUMENTS: "manage_personal_documents",
  VIEW_CONTABILIDAD: "view_contabilidad",
  MANAGE_CONTABILIDAD: "manage_contabilidad",
  VIEW_ASISTENCIA: "view_asistencia",
  MANAGE_ASISTENCIA: "manage_asistencia",
  MANAGE_IMPORTS: "manage_imports",
  MANAGE_TARIFFS: "manage_tariffs",
  MANAGE_MANDATOS: "manage_mandatos",
  VIEW_MANDATOS: "view_mandatos",
  MANAGE_NOTIFICATIONS: "manage_notifications",
  MANAGE_GROUPS: "manage_groups",
  MANAGE_SETTINGS: "manage_settings",
};

export const ROLES = {
  ADMIN: "admin",
  TESORERO: "tesorero",
  PRESIDENTE: "presidente",
  SECRETARIO: "secretario",
  CONTABLE: "contable",
  FALLERO: "fallero",
};

export const ROLE_LABELS = {
  admin: "Administrador",
  tesorero: "Tesorero",
  presidente: "Presidente",
  secretario: "Secretario",
  contable: "Contable",
  fallero: "Fallero",
  viewer: "Fallero", // legacy alias
};

/**
 * Descripciones legibles de cada permiso, agrupadas por dominio.
 * Se usan en la pantalla `Mis permisos` para explicar al usuario
 * qué puede hacer en cada área. Mantener en sync con el backend.
 */
export const PERMISSION_INFO = {
  manage_users: {
    label: "Gestionar usuarios",
    description: "Crear, editar, desactivar usuarios y cambiar sus roles.",
    domain: "Sistema",
  },
  manage_roles: {
    label: "Gestionar roles",
    description: "Modificar la matriz de permisos por rol (futuro).",
    domain: "Sistema",
  },
  manage_organization_config: {
    label: "Configurar la falla",
    description: "Editar nombre, NIF, IBAN, BIC y datos SEPA de la falla.",
    domain: "Sistema",
  },
  manage_members: {
    label: "Gestionar censo",
    description: "Crear, editar y eliminar falleros y sus datos personales.",
    domain: "Personas",
  },
  view_members: {
    label: "Visualizar censo",
    description: "Ver falleros y sus datos personales.",
    domain: "Personas",
  },
  manage_remesas: {
    label: "Gestionar remesas",
    description:
      "Crear lotes de cobro, vincular recibos y descargar SEPA pain.008.",
    domain: "Finanzas",
  },
  view_remesas: {
    label: "Visualizar remesas",
    description: "Ver remesas existentes.",
    domain: "Finanzas",
  },
  manage_recibos: {
    label: "Gestionar recibos y mandatos",
    description:
      "Crear, editar, marcar pagado, generar recibos del próximo mes.",
    domain: "Finanzas",
  },
  view_recibos: {
    label: "Visualizar recibos",
    description: "Visualizar recibos.",
    domain: "Finanzas",
  },
  manage_eventos: {
    label: "Gestionar eventos",
    description: "Crear, editar y eliminar eventos del calendario.",
    domain: "Social",
  },
  manage_documentos: {
    label: "Gestionar documentos",
    description: "Subir y gestionar documentos compartidos de la falla.",
    domain: "Social",
  },
  view_contabilidad: {
    label: "Ver contabilidad",
    description: "Consultar libros, balances e informes contables.",
    domain: "Finanzas",
  },
  manage_contabilidad: {
    label: "Gestionar contabilidad",
    description: "Crear y editar asientos contables, conciliar movimientos.",
    domain: "Finanzas",
  },
  view_asistencia: {
    label: "Ver asistencia",
    description: "Consultar listas de asistencia a eventos.",
    domain: "Social",
  },
  manage_asistencia: {
    label: "Gestionar asistencia",
    description: "Registrar y modificar asistencia de falleros a eventos.",
    domain: "Social",
  },
  manage_imports: {
    label: "Gestionar importaciones",
    description: "Permite importar datos masivamente al sistema.",
    domain: "Sistema",
  },
  manage_tariffs: {
    label: "Gestionar tarifas",
    description: "Registrar y modificar tarifas.",
    domain: "Finanzas",
  },
  manage_mandatos: {
    label: "Gestionar mandatos",
    description:
      "Crear, editar, eliminar mandatos y administrar mandatos bancarios.",
    domain: "Finanzas",
  },
  view_mandatos: {
    label: "Visualizar mandatos",
    description: "Ver mandatos existentes.",
    domain: "Finanzas",
  },
  manage_notifications: {
    label: "Gestionar notificaciones",
    description: "Crear y enviar notificaciones a los usuarios.",
    domain: "Social",
  },
};

/**
 * Acciones de "solo lectura" que cualquier autenticado puede realizar.
 * Se muestran al usuario en la pantalla `Mis permisos` para dejar claro
 * que no todo está bloqueado: hay un mínimo común.
 */
export const COMMON_READ_ACTIONS = [
  { label: "Consultar propios recibos y descargar PDFs", domain: "Finanzas" },
  { label: "Consultar calendario de eventos", domain: "Social" },
  { label: "Cambiar la propia contraseña", domain: "Sistema" },
];
